
import { BillOfMaterialItemModel, ProductModel } from '@/libs/Api';
import Page from '@/Page.vue';
import { Component, Prop } from 'vue-property-decorator';
import { productsStore } from '@/libs/products/+state/store';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import { Guid } from '@/libs/common/functions/guid';

@Component({})
export default class ProductBillOfMaterialList extends Page {
    billOfMaterialItems: BillOfMaterialItemModel[] = [];
    selectedProductId: string | null = null;
    cyclicDependencyDialog = false;
    cyclicProductName = '';

    @Prop() inputValue!: BillOfMaterialItemModel[];
    @Prop() outputProductId!: string;

    mounted() {
        if (this.inputValue) {
            this.billOfMaterialItems = [...this.inputValue];
        }
    }

    get allProducts(): ProductModel[] {
        return (productsStore.useGetter(CrudGetter.Data) as ProductModel[]) ?? [];
    }

    get availableProducts(): any[] {
        const selectedIds = this.billOfMaterialItems.map((x) => x.inputProductId);
        return this.allProducts
            .filter((p) => {
                // Nesmí být vybrán výstupní produkt (zabránění cyklické reference)
                if (p.id === this.outputProductId) {
                    return false;
                }
                // Nesmí být již vybraný v kusovníku
                if (selectedIds.includes(p.id)) {
                    return false;
                }
                return true;
            })
            .map((p) => ({
                id: p.id,
                name: p.name,
                code: p.code,
                label: p.code ? `${p.name} (${p.code})` : p.name,
            }))
            .sort((a, b) => (a.label ?? '').localeCompare(b.label ?? ''));
    }

    getProductName(productId?: string): string {
        const product = this.allProducts.find((p) => p.id === productId);
        return product?.name ?? '';
    }

    getProductCode(productId?: string): string {
        const product = this.allProducts.find((p) => p.id === productId);
        return product?.code ?? '';
    }

    customFilter(item: { name?: string; code?: string }, queryText: string): boolean {
        const searchText = queryText.toLowerCase().trim();
        if (!searchText) {
            return true;
        }
        const name = item.name?.toLowerCase() || '';
        const code = item.code?.toLowerCase() || '';
        return name.includes(searchText) || code.includes(searchText);
    }

    /**
     * Rekurzivně kontroluje, zda přidání komponenty nevytvoří cyklickou závislost
     * @param componentId ID komponenty, kterou chceme přidat
     * @param targetProductId ID produktu, do kterého komponentu přidáváme
     * @param visited Množina již navštívených produktů (pro detekci cyklů)
     * @returns true pokud by vznikla cyklická závislost, jinak false
     */
    wouldCreateCycle(componentId: string, targetProductId: string, visited: Set<string> = new Set()): boolean {
        // Ochrana proti nekonečné rekurzi
        if (visited.has(componentId)) {
            return true;
        }
        visited.add(componentId);

        // Najdeme produkt, který chceme přidat jako komponentu
        const component = this.allProducts.find((p) => p.id === componentId);
        if (!component || !component.billOfMaterialItems) {
            return false;
        }

        // Projdeme všechny komponenty tohoto produktu
        for (const bomItem of component.billOfMaterialItems) {
            if (!bomItem.inputProductId) {
                continue;
            }

            // Pokud komponenta obsahuje náš cílový produkt, vznikne cyklus
            if (bomItem.inputProductId === targetProductId) {
                return true;
            }

            // Rekurzivně zkontrolujeme subkomponenty
            if (this.wouldCreateCycle(bomItem.inputProductId, targetProductId, new Set(visited))) {
                return true;
            }
        }

        return false;
    }

    addItem() {
        if (!this.selectedProductId) {
            return;
        }

        const selectedId = this.selectedProductId;

        // Extra validace - zabránění cyklické reference (preventivní kontrola)
        if (selectedId === this.outputProductId) {
            console.warn('Cannot add product as its own component');
            this.resetSelection();
            return;
        }

        // Kontrola, zda již není přidaný (preventivní kontrola)
        if (this.billOfMaterialItems.some((x) => x.inputProductId === selectedId)) {
            console.warn('Component already added');
            this.resetSelection();
            return;
        }

        // Kontrola cyklické závislosti
        if (this.wouldCreateCycle(selectedId, this.outputProductId)) {
            const selectedProduct = this.allProducts.find((p) => p.id === selectedId);
            this.cyclicProductName = selectedProduct?.name || this.$i18n.t('products.selectedComponent').toString();
            this.cyclicDependencyDialog = true;
            this.resetSelection();
            return;
        }

        const newItem: BillOfMaterialItemModel = {
            id: Guid.EmptyGuid(),
            outputProductId: this.outputProductId,
            inputProductId: selectedId,
        };

        this.billOfMaterialItems = [...this.billOfMaterialItems, newItem];
        this.$emit('change', this.billOfMaterialItems);
        this.resetSelection();
    }

    resetSelection() {
        // Reset autocomplete v nextTick pro zajištění správného vyčištění
        this.$nextTick(() => {
            this.selectedProductId = null;
        });
    }

    removeItem(item: BillOfMaterialItemModel) {
        const items = [...this.billOfMaterialItems];
        const index = items.findIndex((x) => x.inputProductId === item.inputProductId);
        if (index !== -1) {
            items.splice(index, 1);
            this.billOfMaterialItems = items;
            this.$emit('change', this.billOfMaterialItems);
        }
    }
}
